import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Shield, Key, AlertCircle } from 'lucide-react';
import { useDeveloper } from '../../contexts/DeveloperContext';

interface DeveloperAuthProps {
  onSuccess: () => void;
}

export const DeveloperAuth: React.FC<DeveloperAuthProps> = ({ onSuccess }) => {
  const { login, hasPassword, setDeveloperPassword } = useDeveloper();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!hasPassword) {
      // Criar nova senha
      if (password.length < 6) {
        setError('A senha deve ter pelo menos 6 caracteres');
        return;
      }
      
      if (password !== confirmPassword) {
        setError('As senhas não coincidem');
        return;
      }
      
      setIsCreating(true);
      try {
        await setDeveloperPassword(password);
        onSuccess();
      } catch (error) {
        setError('Erro ao salvar senha. Tente novamente.');
        setIsCreating(false);
      }
    } else {
      // Fazer login
      try {
        const success = await login(password);
        if (success) {
          onSuccess();
        } else {
          setError('Senha incorreta');
        }
      } catch (error) {
        setError('Erro ao fazer login. Tente novamente.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl mb-4 shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {hasPassword ? 'Acesso de Desenvolvedor' : 'Configurar Acesso'}
          </h1>
          <p className="text-slate-400">
            {hasPassword 
              ? 'Digite a senha para acessar as ferramentas de desenvolvedor'
              : 'Configure uma senha para acessar as ferramentas de desenvolvedor'
            }
          </p>
        </div>

        {/* Formulário */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo de Senha */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {hasPassword ? 'Senha' : 'Nova Senha'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-12 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  placeholder={hasPassword ? 'Digite sua senha' : 'Mínimo 6 caracteres'}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Campo de Confirmação (apenas na criação) */}
            {!hasPassword && (
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Confirmar Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    placeholder="Confirme sua senha"
                    required
                  />
                </div>
              </div>
            )}

            {/* Erro */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Botão */}
            <button
              type="submit"
              disabled={isCreating}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 disabled:from-slate-600 disabled:to-slate-700 text-white font-medium rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Configurando...</span>
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  <span>{hasPassword ? 'Entrar' : 'Criar Senha'}</span>
                </>
              )}
            </button>
          </form>

          {/* Informações de Segurança */}
          <div className="mt-6 pt-6 border-t border-white/20">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Lock className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-white mb-1">Segurança</h3>
                <p className="text-xs text-slate-400">
                  {hasPassword 
                    ? 'Sua senha está armazenada de forma segura no Firebase'
                    : 'A senha será armazenada de forma segura no Firebase'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
